import { useEffect, useRef, useState } from "react";
import { getPlatformStats, type PlatformStats } from "../../../services/platform";
import RIcon from "../../../components/RIcon";

function buildStats(ps: PlatformStats | null) {
  return [
    { end: ps?.indexed_files ?? 0, suffix: "+", label: "文档索引量", desc: "覆盖研发、临床、注册、质量全业务领域" },
    { end: ps?.total_chats ?? 0, suffix: "+", label: "累计问答次数", desc: "基于企业专有知识库的高精度检索回答" },
    { end: ps?.total_files ?? 0, suffix: "+", label: "知识库文件总数", desc: "PDF、Word、Excel、音视频等全格式支持" },
    { end: ps?.total_users ?? 0, suffix: "+", label: "活跃用户", desc: "已启用账号数（管理员审核通过）" },
  ];
}

const showcaseItems = [
  {
    tag: "AI 智能问答",
    title: "精准召回企业知识，像专家一样回答",
    desc: "基于RAG架构，从海量文档中精准检索，附带来源引用，答案可追溯可验证。",
    image: "https://readdy.ai/api/search-image?query=modern%20medical%20enterprise%20AI%20chat%20interface%20with%20sidebar%20navigation%20showing%20conversation%20history%2C%20clean%20white%20background%20with%20teal%20accent%20colors%2C%20professional%20dashboard%20layout%20with%20message%20bubbles%20showing%20question%20and%20answer%20pairs%20about%20pharmaceutical%20compliance%20GMP%20ICH%20Q10%20regulations%2C%20source%20document%20citations%20shown%20below%20answers%2C%20minimalist%20UI%20design&width=720&height=480&seq=stats_showcase_1&orientation=landscape",
    highlights: ["来源文档可点击验证", "多轮上下文理解", "专业术语精准识别"],
  },
  {
    tag: "智能文档生成",
    title: "一键生成结构化合规报告",
    desc: "预置 GMP/ICH 合规报告模板，填写关键参数后自动生成规范文档，大幅缩短撰写周期。",
    image: "https://readdy.ai/api/search-image?query=modern%20document%20generation%20dashboard%20interface%20with%20template%20selection%20panel%20on%20left%20showing%20report%20templates%20like%20CAPA%20quality%20assessment%20deviation%20report%2C%20form%20fields%20in%20center%20with%20professional%20medical%20document%20preview%20on%20right%20side%2C%20clean%20white%20background%20teal%20green%20accents%2C%20enterprise%20SaaS%20style%20UI%20design&width=720&height=480&seq=stats_showcase_2&orientation=landscape",
    highlights: ["6 类预置合规模板", "参数驱动自动填充", "一键导出 Word/PDF"],
  },
  {
    tag: "文件知识库",
    title: "统一管理，智能索引，权限精细",
    desc: "支持50+格式全量上传，AI自动解析建立索引，部门级权限控制，知识安全不外泄。",
    image: "https://readdy.ai/api/search-image?query=enterprise%20document%20management%20system%20dashboard%20showing%20file%20list%20table%20with%20columns%20for%20file%20name%20type%20size%20permissions%20index%20status%20upload%20date%2C%20filter%20sidebar%20on%20left%20with%20search%20bar%20at%20top%2C%20file%20type%20icons%20PDF%20Word%20Excel%20colorful%20badges%2C%20clean%20professional%20white%20UI%20with%20teal%20accent%20colors%20subtle%20grid%20lines&width=720&height=480&seq=stats_showcase_3&orientation=landscape",
    highlights: ["50+ 格式智能解析", "部门/工种权限控制", "全文检索毫秒响应"],
  },
];

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, fromY = 40, className = "" }: {
  children: React.ReactNode; delay?: number; fromY?: number; className?: string;
}) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0px)" : `translateY(${fromY}px)`,
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function CountUp({ end, suffix, visible }: { end: number; suffix: string; visible: boolean }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!visible) return;
    const duration = 1600;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(end * ease));
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [visible, end]);
  return (
    <span>{count.toLocaleString()}{suffix}</span>
  );
}

type StatItem = ReturnType<typeof buildStats>[number];

function StatCard({ stat, delay }: { stat: StatItem; delay: number }) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0px)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      <div className="flex items-start gap-6">
        <div className="w-1 h-16 bg-gradient-to-b from-[#0EA5A0] to-transparent rounded-full flex-shrink-0" />
        <div>
          <div className="text-5xl font-extrabold text-[#0EA5A0] leading-none mb-2">
            <CountUp end={stat.end} suffix={stat.suffix} visible={visible} />
          </div>
          <div className="text-[#1A2B3C] font-semibold text-base mb-1">{stat.label}</div>
          <div className="text-[#718096] text-sm">{stat.desc}</div>
        </div>
      </div>
    </div>
  );
}

export default function Stats() {
  const [activeShowcase, setActiveShowcase] = useState(0);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    getPlatformStats().then(setPlatformStats).catch(() => {});
  }, []);

  const stats = buildStats(platformStats);

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <Reveal delay={0} fromY={30} className="max-w-3xl mb-14">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5A0]" />
            <span className="text-[#0EA5A0] text-sm font-medium uppercase tracking-wider">平台数据</span>
          </div>
          <p className="text-[#1A2B3C] text-2xl md:text-3xl font-semibold leading-relaxed">
            协和医疗全域AI系统已覆盖研发、临床、注册、质量等核心部门，
            帮助团队将知识管理效率提升数倍。
          </p>
        </Reveal>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-20 gap-y-12">
          {stats.map((s, idx) => (
            <StatCard key={idx} stat={s} delay={idx * 120} />
          ))}
        </div>

        {/* Trust Badges */}
        <Reveal delay={80} fromY={24} className="mt-16 pt-10 border-t border-gray-100">
          <p className="text-center text-[#718096] text-sm mb-8">系统遵循国际标准，符合医疗行业法规要求</p>
          <div className="flex flex-wrap justify-center gap-6">
            {["GMP 合规", "ICH Q10", "ISO 27001", "数据本地化部署", "国密算法支持"].map((badge, i) => (
              <Reveal key={badge} delay={i * 60} fromY={16}>
                <div className="flex items-center gap-2 bg-[#F4F8FB] rounded-lg px-4 py-2.5">
                  <RIcon name="shield-check-line" className="text-[#0EA5A0] text-sm" />
                  <span className="text-[#4A5568] text-sm font-medium">{badge}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Reveal>

        {/* Product Showcase */}
        <Reveal delay={0} fromY={30} className="mt-24">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5A0]" />
            <span className="text-[#0EA5A0] text-sm font-medium uppercase tracking-wider">产品界面</span>
          </div>
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            {/* Tab selector */}
            <div className="lg:w-80 flex-shrink-0 space-y-3">
              <h3 className="text-[#1A2B3C] text-xl font-semibold mb-6 leading-snug">
                专为医疗合规场景<br />深度打磨的产品体验
              </h3>
              {showcaseItems.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveShowcase(idx)}
                  className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    activeShowcase === idx
                      ? "border-[#0EA5A0] bg-[#0EA5A0]/5"
                      : "border-gray-100 hover:border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      activeShowcase === idx ? "bg-[#0EA5A0] text-white" : "bg-gray-100 text-[#718096]"
                    }`}>{item.tag}</span>
                  </div>
                  <p className={`text-sm font-semibold mt-1.5 ${activeShowcase === idx ? "text-[#0EA5A0]" : "text-[#1A2B3C]"}`}>
                    {item.title}
                  </p>
                  {activeShowcase === idx && (
                    <p className="text-xs text-[#718096] mt-1.5 leading-relaxed">{item.desc}</p>
                  )}
                </button>
              ))}
            </div>

            {/* Screenshot + highlights */}
            <div className="flex-1 min-w-0">
              {showcaseItems.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    opacity: activeShowcase === idx ? 1 : 0,
                    transform: activeShowcase === idx ? "translateY(0px)" : "translateY(16px)",
                    transition: "opacity 0.4s ease, transform 0.4s cubic-bezier(0.22,1,0.36,1)",
                    display: activeShowcase === idx ? "block" : "none",
                  }}
                >
                  {/* Browser chrome mockup */}
                  <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white">
                    {/* Browser bar */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-[#F4F8FB] border-b border-gray-200">
                      <span className="w-3 h-3 rounded-full bg-red-400" />
                      <span className="w-3 h-3 rounded-full bg-amber-400" />
                      <span className="w-3 h-3 rounded-full bg-emerald-400" />
                      <div className="flex-1 mx-3 bg-white rounded-md px-3 py-1 text-xs text-[#718096] border border-gray-200 flex items-center gap-1.5">
                        <RIcon name="lock-line" className="text-emerald-500 text-xs" />
                        app.rhinomed-ai.com
                      </div>
                    </div>
                    {/* Screenshot */}
                    <div className="w-full h-64 md:h-80 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.tag}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-3 mt-5">
                    {item.highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 bg-[#F4F8FB] rounded-lg px-4 py-2.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <RIcon name="check-line" className="text-[#0EA5A0] text-sm" />
                        </div>
                        <span className="text-[#4A5568] text-sm font-medium">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

      </div>
    </section>
  );
}
